import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { GraphPhoto } from '@/contexts/FamilyGraphContext';

interface Props {
  items: GraphPhoto[];
  startIndex: number;
  onClose: () => void;
}

/**
 * Validate a media URL before passing it to <img> / <video>. Accept the
 * schemes the upload pipeline can produce (http / https / blob / data).
 * Anything else (null, empty string, "undefined", `file://`, javascript:)
 * is rejected so the lightbox can show a polite "could not be loaded"
 * fallback instead of a broken element. Pure helper — no DB writes,
 * no schema changes, no impact on tree relationships or spouse links.
 */
const isSafeMediaUrl = (u: string | null | undefined): boolean => {
  if (!u || typeof u !== 'string') return false;
  const trimmed = u.trim();
  if (!trimmed) return false;
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('data:')
  );
};

/**
 * Full-screen viewer for KinsCircle profile media. Renders photos as <img>
 * and videos as a controllable <video>. Supports left / right navigation
 * within the same gallery and the Esc key to close.
 *
 * BLANK-PAGE BUG FIX:
 *  - Validates the current item's URL before rendering the media element
 *    so a stale storage path / null url can't blow up the React tree.
 *  - Adds onError handlers so a runtime load failure swaps to a polite
 *    inline "This memory could not be loaded." card and the user can
 *    still navigate with the prev/next chevrons or close back to the tree.
 *  - Defensive guards on items[] / startIndex so an empty or malformed
 *    invocation just closes itself instead of crashing the parent.
 */
const MediaLightbox: React.FC<Props> = ({ items, startIndex, onClose }) => {
  const safeStart = Math.max(0, Math.min(startIndex || 0, (items?.length ?? 1) - 1));
  const [idx, setIdx] = useState(safeStart);
  // Track per-item runtime load failures so we render the fallback card
  // instead of the broken <img> / <video>. Keyed by photo id so swiping
  // back to a working item still shows it correctly.
  const [failedIds, setFailedIds] = useState<Record<string, true>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((p) => Math.min((items?.length ?? 1) - 1, p + 1));
      if (e.key === 'ArrowLeft') setIdx((p) => Math.max(0, p - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items?.length, onClose]);

  // Defensive: empty list / bad input → close gracefully instead of
  // returning null and leaving the user staring at a blank dimmed page.
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div
        className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <div className="font-serif font-bold text-lg text-slate-900 mb-1">
            Nothing to show
          </div>
          <p className="text-sm text-slate-600 mb-4">This memory could not be loaded.</p>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-2.5 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const cur = items[idx];
  if (!cur) {
    // Out-of-range index — also a graceful close.
    return null;
  }

  const urlOk = isSafeMediaUrl(cur.url);
  const runtimeFailed = !!failedIds[cur.id];
  const showMedia = urlOk && !runtimeFailed;

  const markFailed = (id: string) =>
    setFailedIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {idx > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((p) => Math.max(0, p - 1));
          }}
          className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Previous"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {idx < items.length - 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((p) => Math.min(items.length - 1, p + 1));
          }}
          className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
          aria-label="Next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      <div
        className="max-w-full max-h-full flex flex-col items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        {showMedia ? (
          cur.mediaType === 'video' ? (
            <video
              key={cur.id}
              src={cur.url}
              controls
              autoPlay
              playsInline
              onError={() => markFailed(cur.id)}
              className="max-w-[95vw] max-h-[80vh] rounded-lg bg-black"
            />
          ) : (
            <img
              key={cur.id}
              src={cur.url}
              alt={cur.caption || 'Family photo'}
              onError={() => markFailed(cur.id)}
              className="max-w-[95vw] max-h-[80vh] object-contain rounded-lg"
            />
          )
        ) : (
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-[90vw] p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <div className="font-serif font-bold text-lg text-slate-900 mb-1">
              This memory could not be loaded.
            </div>
            <p className="text-xs text-slate-500 mb-4">
              The file may be missing, private, or temporarily unavailable. Try the next item or
              close this viewer.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-2 px-4 text-sm"
            >
              Close
            </button>
          </div>
        )}
        {(cur.caption || cur.title) && (
          <div className="text-white/90 text-sm text-center max-w-2xl px-3">
            {cur.title && <div className="font-semibold">{cur.title}</div>}
            {cur.caption && <div className="text-white/75 text-xs mt-0.5">{cur.caption}</div>}
          </div>
        )}
        <div className="text-[10px] text-white/55">
          {idx + 1} / {items.length}
        </div>
      </div>
    </div>
  );
};

export default MediaLightbox;
